import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import * as RadixLabel from '@radix-ui/react-label';
import * as RadixSelect from '@radix-ui/react-select';

// Utils
import bemify from 'utils/bemify';

// Components
import Icon from 'components/icon';

// Styles
import stylesModule from './styles.module.scss';


const styles = bemify(stylesModule);

const Select = forwardRef(({
  className = null,
  description = null,
  disabled = false,
  icon = null,
  id = null,
  instructions = 'Select an option',
  label = null,
  native = false,
  noBorder = false,
  onChange = () => {},
  options = [],
  required = false,
  rounded = false,
  selectedOption = null,
  triggerClassName = null,
  validationState = 'default'
}, ref) => {
  if (!Array.isArray(options) || !options.length) return null;

  const handleChange = (option) => {
    if (!disabled) {
      onChange(option);
    }
  };

  let selectElement = (
    <RadixSelect.Root
      ref={ref}
      value={selectedOption || options[0]}
      onValueChange={handleChange}
    >
      <RadixSelect.Trigger className={`${styles.select__select}${
        noBorder ? ` ${styles.select__select_noBorder}` : ''}${
        triggerClassName ? ` ${triggerClassName}` : ''}${
        rounded ? ` ${styles.select__select_rounded}` : ''}${
        validationState === 'error' && !noBorder ? ` ${styles.select__select_error}` : ''}`}
      >
        {!!icon && (
          <Icon
            className={styles.select__icon}
            name={icon}
          />
        )}
        <RadixSelect.Value>
          {selectedOption}
        </RadixSelect.Value>
        <RadixSelect.Icon>
          <Icon
            className={styles.select__chevron}
            name="chevron-down"
          />
        </RadixSelect.Icon>
      </RadixSelect.Trigger>
      <RadixSelect.Portal>
        <RadixSelect.Content className={styles.select__content}>
          <RadixSelect.Viewport>
            {options.map(entry => (
              <RadixSelect.Item
                key={entry.value}
                className={styles.select__option}
                value={entry.value}
              >
                {!!entry.icon && (
                  <Icon
                    className={styles.select__icon}
                    name={entry.icon}
                  />
                )}
                <RadixSelect.ItemText>
                  {entry.text || entry.value}
                </RadixSelect.ItemText>
              </RadixSelect.Item>
            ))}
          </RadixSelect.Viewport>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  );

  if (native) {
    selectElement = (
      <>
        <select
          ref={ref}
          className={`${styles.select__select}${
            rounded ? ` ${styles.select__select_rounded}` : ''}${
            validationState === 'error' ? ` ${styles.select__select_error}` : ''}`}
          disabled={disabled}
          id={id}
          value={selectedOption}
          onBlur={event => handleChange(event?.target?.value ?? '')}
          onChange={event => handleChange(event?.target?.value ?? '')}
        >
          {!!instructions && (
            <option
              key={-1}
              disabled
              value=""
            >
              {instructions}
            </option>
          )}
          {options.map(option => (
            <option
              key={option.value}
              disabled={disabled}
              value={option.value}
            >
              {option.text || option.value}
            </option>
          ))}
        </select>
        <Icon
          className={styles.select__chevron}
          name="chevron-down"
        />
      </>
    );
  }

  return (
    <div className={`${styles.selectWrapper}${
      disabled ? ` ${styles.select_disabled}` : ''}`}
    >
      {!!label && (
        <RadixLabel.Root
          className={`${styles.select__label}${disabled ? ` ${styles.select__label_disabled}` : ''}`}
          htmlFor={id}
        >
          {label}
          {required && (
            <span className={`${styles.select__labelRequiredMark}${
              validationState === 'error' ? ` ${styles.select__labelRequiredMark_error}` : ''}`}
            >
              {' '}
              *
            </span>
          )}
        </RadixLabel.Root>
      )}
      <div className={`${styles.select}${className ? ` ${className}` : ''}`}>
        {selectElement}
      </div>
      {!!description && (
        <span className={`${styles.select__description}${validationState === 'error' ? ` ${styles.select__description_error}` : ''}`}>
          {description}
        </span>
      )}
    </div>
  );
});

Select.displayName = 'Select';

Select.propTypes = {
  className: PropTypes.string,
  description: PropTypes.string,
  disabled: PropTypes.bool,
  icon: PropTypes.string,
  id: PropTypes.string,
  instructions: PropTypes.string,
  label: PropTypes.string,
  native: PropTypes.bool,
  noBorder: PropTypes.bool,
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.shape({
    text: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  })),
  required: PropTypes.bool,
  rounded: PropTypes.bool,
  selectedOption: PropTypes.string,
  triggerClassName: PropTypes.string,
  validationState: PropTypes.oneOf(['default', 'error'])
};

export default Select;
